import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';
import {
  BaseButton,
  Modal,
  POLICY_URLS,
  policyUrl,
  useAuth,
  useLang,
  useT,
} from '@repo/common';
import { deleteAccount } from '../../../../api/user/user.api';
import { isBusinessRole } from '../../../Gnb/jobNavigationConfig';

const FinalDeleteConfirm = ({ onSuccess, $isMobile }) => {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const profileImageUrl = `${process.env.REACT_APP_ASSETS_URL}/img-profile.svg`;
  const { user } = useAuth();
  const { firstName, lastName } = user;

  const [profileImage, setProfileImage] = useState(null);
  const [showBlockingModal, setShowBlockingModal] = useState(false);
  const [counts, setCounts] = useState({
    booking: 0,
    jobApply: 0,
  });

  useEffect(() => {
    if (user?.profileUrl) {
      setProfileImage(user.profileUrl);
    }
  }, [user]);

  const DELETE_WARNINGS = [
    t(
      'Your data will be deleted immediately upon deactivation.',
      '탈퇴 즉시 회원님의 데이터가 삭제됩니다.',
    ),
    <>
      {t(
        'Some information may be retained in accordance with applicable laws and our',
        '관련 법령 및 다음 정책에 따라 일부 정보가 보관될 수 있습니다:',
      )}{' '}
      <a
        href={
          isBusinessRole(user?.role)
            ? POLICY_URLS.businessPrivacy
            : policyUrl('privacy', lang)
        }
        target='_blank'
        rel='noopener noreferrer'
        className='underline text-text-tertiary hover:text-text-primary'
      >
        {t('Privacy Policy', '개인정보 처리방침')}
      </a>
      .
    </>,
    t(
      'All other personal data will be permanently removed.',
      '그 외 모든 개인정보는 영구적으로 삭제됩니다.',
    ),
  ];

  const handleDelete = async (force = false) => {
    try {
      await deleteAccount(force);
      onSuccess?.();
    } catch (err) {
      if (err?.response?.status === 409) {
        const data = err.response.data?.data;

        setCounts({
          booking: data?.bookingCount || 0,
          jobApply: data?.jobApplyCount || 0,
        });

        setShowBlockingModal(true);
      }
    }
  };

  return (
    <div className='flex flex-col items-start tabletH:items-center justify-center h-full w-full tabletH:w-[460px]'>
      <div className='flex pb-6 typo-title1 tabletH:pb-10 tabletH:typo-h5'>
        {t('Delete your account?', '계정을 삭제하시겠어요?')}
      </div>

      <div className='flex flex-col items-center gap-2 pb-6 tabletH:pb-[30px]'>
        <div className='flex items-center justify-center size-[80px] tabletH:size-[124px] cursor-pointer group tabletH:mb-[14px]'>
          {profileImage ? (
            <img
              src={profileImage}
              alt='profile preview'
              className='w-full h-full rounded-full object-cover'
            />
          ) : (
            <img
              src={profileImageUrl}
              alt='default profile'
              className='w-full h-full rounded-full'
            />
          )}
        </div>

        <div className='typo-b14 tabletH:typo-b20Strong text-left tabletH:text-center text-text-primary w-full'>
          {firstName} {lastName}
        </div>
      </div>

      <div className='tabletH:pb-10'>
        <div className='flex flex-col p-2 tabletH:p-4 items-start gap-1.5 tabletH:gap-2 rounded-lg bg-grayscale-gray01 text-text-tertiary'>
          {DELETE_WARNINGS.map((warning, index) => (
            <div key={index} className='flex items-start typo-b14 gap-1'>
              <span className='text-icon-placeholder'>
                <CheckIcon />
              </span>
              <span>{warning}</span>
            </div>
          ))}
        </div>
      </div>

      <div
        className='flex flex-col items-start gap-1 w-full
        fixed bottom-0 left-0 p-4 bg-grayscale-gray00 border-t border-grayscale-gray02 tabletH:border-none
        tabletH:static tabletH:px-0 tabletH:pb-0 tabletH:bg-transparent'
      >
        <BaseButton
          hideIcon
          width='100%'
          type='warning'
          size='xl'
          onClick={() => handleDelete(false)}
        >
          {t('Delete account', '계정 삭제')}
        </BaseButton>
        <BaseButton
          hideIcon
          width='100%'
          type='text_basic'
          size='xl'
          borderclass='border-none'
          onClick={() => navigate(-1)}
        >
          {t('cancel', '취소')}
        </BaseButton>
      </div>

      {showBlockingModal && (
        <Modal
          isOpen
          type='warning'
          title={
            $isMobile
              ? t(
                  'Please review before deleting\nyour account',
                  '계정 삭제 전에\n확인해 주세요',
                )
              : t(
                  'Please review\nbefore deleting your account',
                  '계정 삭제 전에\n확인해 주세요',
                )
          }
          subText={
            <div className='whitespace-pre-line'>
              <div>
                {t(
                  'You have active items that will be canceled if you continue:',
                  '계속 진행하면 취소되는 진행 중인 항목이 있습니다:',
                )}
              </div>
              <div className='tabletH:mt-2'>
                <span className='text-text-accent'>{counts.booking}</span>{' '}
                {t('workshop reservations', '개의 워크숍 예약')}
              </div>
              <div>
                <span className='text-text-accent'>{counts.jobApply}</span>{' '}
                {t('job application', '개의 일자리 지원')}
              </div>
              <div className='mt-4 tabletH:mt-2'>
                {t('Review or cancel them in', '계정을 삭제하기 전에')}{' '}
                <span className='underline'>{t('Activities', '활동 내역')}</span>
              </div>
              {t(
                'before deleting your account.',
                '에서 확인하거나 취소해 주세요.',
              )}
            </div>
          }
          leftButtonLabel={t('Go back', '돌아가기')}
          rightButtonLabel={t('Delete anyway', '그래도 삭제')}
          leftHideIcon
          rightHideIcon
          onClickLeftBtn={() => setShowBlockingModal(false)}
          onClickRightBtn={() => handleDelete(true)}
          bgclass={$isMobile ? 'bg-grayscale-gray00' : 'bg-grayscale-gray01'}
        />
      )}
    </div>
  );
};

export default FinalDeleteConfirm;
